import moment from 'moment'
import Customer, { CustomerModel } from '@/model/customer/Customer'
import MenuGroup, { MenuGroupModel } from '@/model/menu/MenuGroup'
import MenuGroupSlot, { MenuGroupSlotModel } from '@/model/menu/MenuGroupSlot'
import Business, { BusinessModel } from '@/model/business/Business'
import BoarderGroup, { BoarderGroupModel } from '@/model/boarder/BoarderGroup'
import Boarder, { BoarderModel } from '@/model/boarder/Boarder'
import { ReservationStatus } from '@/types/enums'

interface MenuGroupWithSlotsModel {
  menuGroup: Partial<MenuGroupModel>,
  slotList: MenuGroupSlotWithCountModel[]
}

export interface MenuGroupWithSlots {
  menuGroup: MenuGroup,
  slotList: MenuGroupSlotWithCount[]
}

interface MenuGroupSlotWithCountModel {
  menuGroupSlot: Partial<MenuGroupSlotModel>
  count: number
}

export interface MenuGroupSlotWithCount {
  menuGroupSlot: MenuGroupSlot
  count: number
}

interface BoarderSlotSelectionModel {
  boarder: Partial<BoarderModel>
  slotSelectionList: SlotSelectionModel[]
}

export interface BoarderSlotSelection {
  boarder: Boarder
  slotSelectionList: SlotSelection[]
}

interface SlotSelectionModel {
  menuGroup: Partial<MenuGroupModel>
  reservationStatus: ReservationStatus
  selectedSlot: Partial<MenuGroupSlotModel>
}

export interface SlotSelection {
  menuGroup: MenuGroup
  reservationStatus: ReservationStatus
  selectedSlot: MenuGroupSlot | undefined
}

export interface ReservationByDayModel {
  business: Partial<BusinessModel>
  customer: Partial<CustomerModel>
  boarderGroup: Partial<BoarderGroupModel>
  topMenuGroup: Partial<MenuGroupModel>
  menuGroupList: MenuGroupWithSlotsModel[]
  boarderList: BoarderSlotSelectionModel[]
  date: Date
  toBeDeliveredCount: number
  deliveredCount: number
  notDeliveredCount: number
  canceledCount: number
}

export type ReservationByDayResponse = ReservationByDayModel[]

function createMenuGroupList (list: MenuGroupWithSlotsModel[]): MenuGroupWithSlots[] {
  return list.map((mgi: MenuGroupWithSlotsModel) => ({
    menuGroup: MenuGroup.createFromModel(mgi.menuGroup),

    slotList: mgi.slotList.map((mgsi: MenuGroupSlotWithCountModel): MenuGroupSlotWithCount => ({
      menuGroupSlot: MenuGroupSlot.createFromModel(mgsi.menuGroupSlot),
      count: mgsi.count
    }))
  }))
}

function createSlotSelectionList (list: SlotSelectionModel[]): SlotSelection[] {
  return list.map((i: SlotSelectionModel) => ({
    menuGroup: MenuGroup.createFromModel(i.menuGroup),
    reservationStatus: i.reservationStatus,
    selectedSlot: i.selectedSlot ? MenuGroupSlot.createFromModel(i.selectedSlot) : undefined
  }))
}

function createBoarderList (list: BoarderSlotSelectionModel[]): BoarderSlotSelection[] {
  return list.map((i: BoarderSlotSelectionModel) => ({
    boarder: Boarder.createFromModel(i.boarder),
    slotSelectionList: createSlotSelectionList(i.slotSelectionList)
  }))
}

export default class ReservationByDay {
  private constructor (
    readonly business: Business,
    readonly customer: Customer,
    readonly boarderGroup: BoarderGroup,
    readonly topMenuGroup: MenuGroup,
    readonly menuGroupList: MenuGroupWithSlots[],
    readonly boarderList: BoarderSlotSelection[],
    readonly date: Date,
    readonly toBeDeliveredCount: number,
    readonly deliveredCount: number,
    readonly notDeliveredCount: number,
    readonly canceledCount: number,
  ) {}

  public static createFromResponse (responseData: ReservationByDayResponse): ReservationByDay[] {
    return responseData.map((data: ReservationByDayModel): ReservationByDay => {
      return new ReservationByDay(
        Business.createFromModel(data.business),
        Customer.createFromModel(data.customer),
        BoarderGroup.createFromModel(data.boarderGroup),
        MenuGroup.createFromModel(data.topMenuGroup),

        createMenuGroupList(data.menuGroupList),
        createBoarderList(data.boarderList),

        moment(data.date).toDate(),
        data.toBeDeliveredCount,
        data.deliveredCount,
        data.notDeliveredCount,
        data.canceledCount
      )
    })
  }
}
